/**
 * The landing module's state and actions in the browser. Live: state.ext.landing, kept
 * current by SSE 'ext' events, and the /api/landing routes. Demo (?mock=1): the same calls
 * applied to demo data by mockActions.ts.
 */
import { useEffect, useState } from 'react';
import { effectiveLandingConfig } from '@orgus/protocol';
import type { Deployment, DeploymentKind, DeployRequest, DeployFreezeWindow, Id, Landing, LandingProjectConfig, LandingProjectConfigInput, LandingQueue, LandingState, LandingSubmitRequest } from '@orgus/protocol';
import { toUiError, isModuleAbsent } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { api, apiGet } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { isMockStill } from '../../lib/mock.ts';
import { createMockLanding } from './mock.ts';
import {
  mockCancel,
  mockDeploy,
  mockFinishDeploys,
  mockFreeze,
  mockPause,
  mockReorder,
  mockRetry,
  mockSaveConfig,
  mockSubmit,
  mockTick,
  mockUnfreeze,
} from './mockActions.ts';

const MOCK_ACTOR = 'owner';

const isMock = () => useOrgus.getState().mode === 'mock';

function current(): LandingState | undefined {
  return (useOrgus.getState().ext as Record<string, unknown> | undefined)?.landing as LandingState | undefined;
}

/**
 * Replace the landing slice of the store (demo mode, or after a direct GET). Through applyExt,
 * so it lands in `full.ext` as well as the scoped view: written to the view alone, the next live
 * update rebuilt the view from `full` without it, the slice vanished, demo data was created
 * afresh and every open dialog and drawer on Pipelines remounted about once a second.
 */
export function setLandingState(s: LandingState): void {
  useOrgus.getState().applyExt({ ext: 'landing', type: 'state', data: s });
}

function mockState(): LandingState {
  const s = current();
  if (s) return s;
  const fresh = createMockLanding();
  setLandingState(fresh);
  return fresh;
}

/** The landing state from the store. */
export function useLandingSlice(): LandingState | undefined {
  return useOrgus((s) => (s.ext as Record<string, unknown> | undefined)?.landing as LandingState | undefined);
}

export interface LandingLoad {
  state: LandingState | undefined;
  /** loading: nothing yet. error: the hub has no landing module or did not answer. */
  phase: 'loading' | 'ready' | 'error';
  error: UiError | null;
  retry: () => Promise<void>;
}

/** The landing state with its loading and error states. Demo mode fills it with demo data. */
export function useLanding(): LandingLoad {
  const state = useLandingSlice();
  const mode = useOrgus((s) => s.mode);
  const storePhase = useOrgus((s) => s.phase);
  const storeError = useOrgus((s) => s.loadError);
  const [error, setError] = useState<UiError | null>(null);

  const fetchOnce = async () => {
    setError(null);
    try {
      setLandingState(await apiGet<LandingState>('/api/landing'));
    } catch (e) {
      const ui = toUiError(e);
      setError(
        ui.status === 404 && !isModuleAbsent(ui)
          ? { what: 'This hub does not run the landing queue.', why: 'It answered 404 for /api/landing, so it is an older version.', next: 'Update Orgus on the hub machine and restart it.', status: 404 }
          : ui,
      );
    }
  };

  useEffect(() => {
    if (state) return;
    if (mode === 'mock') {
      if (storePhase === 'ready') setLandingState(createMockLanding());
      return;
    }
    // The hub sends state.ext.landing with the first state; ask directly when it did not.
    if (storePhase === 'ready') void fetchOnce();
  }, [state, mode, storePhase]);

  if (state) return { state, phase: 'ready', error: null, retry: fetchOnce };
  if (storePhase === 'error') return { state, phase: 'error', error: storeError, retry: fetchOnce };
  if (error) return { state, phase: 'error', error, retry: fetchOnce };
  return { state, phase: 'loading', error: null, retry: fetchOnce };
}

/** Demo mode: move landings along and finish deploys, so the tabs are live. */
export function useMockLandingTicker(): void {
  const mode = useOrgus((s) => s.mode);
  useEffect(() => {
    // Still demo data (?still=1, screenshots and visual tests) never moves.
    if (mode !== 'mock' || isMockStill()) return;
    const t = setInterval(() => {
      const s = current();
      if (!s) return;
      const now = Date.now();
      const a = mockTick(s, now) ?? s;
      const b = mockFinishDeploys(a, now) ?? a;
      if (b !== s) setLandingState(b);
    }, 1000);
    return () => clearInterval(t);
  }, [mode]);
}

/** A project's settings: the saved ones, else the defaults the hub would use. */
/** Saved settings, else the defaults with the verify command detected from the repository (ADR 0066). */
export function projectConfig(state: LandingState | undefined, projectId: Id, defaultBranch: string): LandingProjectConfig {
  return effectiveLandingConfig(state, projectId, defaultBranch);
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

const enc = encodeURIComponent;

export async function submitLanding(req: LandingSubmitRequest, defaultBranch: string): Promise<Landing> {
  if (isMock()) {
    const r = mockSubmit(mockState(), req, defaultBranch, MOCK_ACTOR, Date.now());
    setLandingState(r.state);
    return r.landing;
  }
  return (await api<{ landing: Landing }>('/api/landing/landings', { method: 'POST', body: req })).landing;
}

export async function cancelLanding(id: Id): Promise<void> {
  if (isMock()) return setLandingState(mockCancel(mockState(), id, MOCK_ACTOR, Date.now()));
  await api(`/api/landing/landings/${enc(id)}/cancel`, { method: 'POST', body: {} });
}

export async function retryLanding(id: Id, front = false): Promise<void> {
  if (isMock()) return setLandingState(mockRetry(mockState(), id, MOCK_ACTOR, Date.now(), front));
  await api(`/api/landing/landings/${enc(id)}/retry`, { method: 'POST', body: { front } });
}

export async function reorderQueue(queueId: string, order: Id[]): Promise<LandingQueue | void> {
  if (isMock()) return setLandingState(mockReorder(mockState(), queueId, order));
  return (await api<{ queue: LandingQueue }>(`/api/landing/queues/${enc(queueId)}/reorder`, { method: 'POST', body: { order } })).queue;
}

/** "Allow landing on <machine>": the hub asks that machine's person again, in a dialog there. */
export async function askLandingConsent(queueId: string, machineId: Id): Promise<void> {
  if (isMock()) {
    const s = mockState();
    return setLandingState({ ...s, queues: s.queues.map((q) => (q.id === queueId ? { ...q, consent: (q.consent ?? []).map((c) => (c.machineId === machineId ? { ...c, state: 'asking' as const, askedAt: Date.now(), next: undefined } : c)) } : q)) });
  }
  await api(`/api/landing/queues/${enc(queueId)}/consent`, { method: 'POST', body: { machineId } });
}

export async function setQueuePaused(queueId: string, paused: boolean): Promise<void> {
  if (isMock()) return setLandingState(mockPause(mockState(), queueId, paused, MOCK_ACTOR, Date.now()));
  await api(`/api/landing/queues/${enc(queueId)}/${paused ? 'pause' : 'resume'}`, { method: 'POST', body: {} });
}

export async function saveConfig(projectId: Id, input: LandingProjectConfigInput, base: LandingProjectConfig): Promise<LandingProjectConfig> {
  if (isMock()) {
    const next: LandingProjectConfig = {
      ...base,
      ...input,
      environments: (input.environments ?? base.environments).map((e) => ({ ...e, freezes: base.environments.find((b) => b.name === e.name)?.freezes ?? [] })),
      projectId,
      updatedBy: MOCK_ACTOR,
      updatedAt: Date.now(),
    } as LandingProjectConfig;
    setLandingState(mockSaveConfig(mockState(), next));
    return next;
  }
  return (await api<{ config: LandingProjectConfig }>(`/api/landing/config/${enc(projectId)}`, { method: 'PUT', body: input })).config;
}

export async function requestDeploy(kind: DeploymentKind, projectId: Id, env: string, body: DeployRequest, config: LandingProjectConfig): Promise<Deployment> {
  if (isMock()) {
    const r = mockDeploy(mockState(), kind, projectId, env, body, config, MOCK_ACTOR, Date.now());
    setLandingState(r.state);
    return r.deployment;
  }
  return (await api<{ deployment: Deployment }>(`/api/landing/environments/${enc(projectId)}/${enc(env)}/${kind}`, { method: 'POST', body })).deployment;
}

export async function addFreeze(projectId: Id, env: string, input: Omit<DeployFreezeWindow, 'id' | 'createdAt' | 'createdBy'>, config: LandingProjectConfig): Promise<void> {
  if (isMock()) return setLandingState(mockFreeze(mockState(), config, env, input, MOCK_ACTOR, Date.now()));
  await api(`/api/landing/environments/${enc(projectId)}/${enc(env)}/freezes`, { method: 'POST', body: input });
}

export async function removeFreeze(projectId: Id, env: string, id: Id, config: LandingProjectConfig): Promise<void> {
  if (isMock()) return setLandingState(mockUnfreeze(mockState(), config, env, id));
  await api(`/api/landing/environments/${enc(projectId)}/${enc(env)}/freezes/${enc(id)}`, { method: 'DELETE' });
}
